<script setup lang="ts">
import type { BoxLayout, DisplayMode } from "@shopware-pwa/composables-next";
import {
  getProductName,
  getProductRoute,
  getProductFromPrice,
  getSmallestThumbnailUrl,
} from "@shopware-pwa/helpers-next";
import type { Schemas } from "#shopware";
import { ApiClientError } from "@shopware/api-client";

const { pushSuccess, pushError } = useNotifications();
const { t } = useI18n();
const { getErrorsCodes } = useCartNotification();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const { resolveCartError } = useCartErrorParamsResolver();
const miniCartModal = useMiniCartModal();
const isLoading = ref(false);

const props = withDefaults(
  defineProps<{
    product: Schemas["Product"];
    layoutType?: BoxLayout;
    displayMode?: DisplayMode;
  }>(),
  {
    layoutType: "standard",
    displayMode: "standard",
  }
);
const { product } = toRefs(props);
const { addToCart, isInCart, count } = useAddToCart(product);
const { addToWishlist, removeFromWishlist, isInWishlist } = useProductWishlist(
  product.value.id
);

const toggleWishlistProduct = async () => {
  isLoading.value = true;
  try {
    if (!isInWishlist.value) {
      await addToWishlist();
      pushSuccess(
        t(`product.messages.addedToWishlist`, {
          p: props.product?.translated.name,
        })
      );
    } else {
      await removeFromWishlist();
      pushSuccess(
        t(`product.messages.removedFromWishlist`, {
          p: props.product?.translated.name,
        })
      );
    }
  } catch (error) {
    if (error instanceof ApiClientError) {
      const reason = error.details.errors?.[0]?.detail
        ? `Reason: ${error.details.errors?.[0]?.detail}`
        : "";
      pushError(
        `${props.product?.translated.name} cannot be ${isInWishlist.value ? "removed from" : "added to"} wishlist.\n${reason}`,
        {
          timeout: 5000,
        }
      );
    }
  } finally {
    isLoading.value = false;
  }
};

const isAddToCartProcessing = ref(false);
const addToCartProxy = async () => {
  isAddToCartProcessing.value = true;
  await addToCart();
  const errors = getErrorsCodes();

  errors?.forEach((element) => {
    const { messageKey, params } = resolveCartError(element);
    pushError(t(`errors.${messageKey}`, params as Record<string, unknown>));
    isAddToCartProcessing.value = false;
  });

  if (!errors.length)
    pushSuccess(
      t(`cart.messages.addedToCart`, { p: props.product?.translated.name })
    );
  isAddToCartProcessing.value = false;

  miniCartModal.open();
};

const fromPrice = getProductFromPrice(props.product);

const imageElement = useTemplateRef("imageElement");
const { height } = useElementSize(imageElement);

const DEFAULT_THUMBNAIL_SIZE = 10;
function roundUp(num: number) {
  return num ? Math.ceil(num / 100) * 100 : DEFAULT_THUMBNAIL_SIZE;
}

const srcPath = computed<string>(() => {
  if (product.value?.cover?.media.thumbnails?.length > 0) {
    let thumb = `${getSmallestThumbnailUrl(product.value?.cover?.media)}`;
    if (thumb) {
      return thumb;
    }
  }
  return `${product.value?.cover?.media.url}`;
});
</script>

<template>
  <div class="product-wrap relative" data-testid="product-box">
    <div class="mb-4 prod-img p-0 md:p-0 relative h-[280px]">
      <RouterLink
        :to="formatLink(getProductRoute(product))"
        class="overflow-hidden"
      >
        <figure
          class="flex items-center overflow-hidden justify-center h-[280px]"
        >
          <NuxtImg
            ref="imageElement"
            provider="ipx"
            loading="lazy"
            format="webp"
            :src="srcPath"
            :alt="getProductName({ product }) || ''"
            class="img-cover mCS_img_loaded h-full w-full object-contain"
            :height="product?.cover?.media?.metaData?.height"
            :width="product?.cover?.media?.metaData?.width"
            data-testid="product-box-img"
          />
        </figure>
      </RouterLink>

      <div class="absolute top-0 left-0">
        <div
          v-if="product.markAsTopseller"
          class="prodd-new py-2 px-4 text-white bg-dark-bg rounded text-xs"
        >
          <span>{{ $t("product.badges.topseller") }}</span>
        </div>
        <div
          v-if="product.isNew"
          class="prodd-new py-2 px-4 mt-2 text-white bg-dark-bg rounded text-xs"
        >
          <span>{{ $t("product.badges.new") }}</span>
        </div>
      </div>
    </div>
    <div class="product-detail">
      <h6
        class="text-sm md:text-base font-bold max-w-[90%] truncate"
        v-if="getProductName({ product })"
      >
        <RouterLink :to="formatLink(getProductRoute(product))">
          {{ getProductName({ product }) }}
        </RouterLink>
      </h6>

      <div class="pro-num-cart flex items-center justify-between">
        <SharedListingProductPrice
          :product="product"
          class="text-base font-bold"
          data-testid="product-box-product-price"
        />

        <button
          v-if="!fromPrice && !product.customFields?.set_as_set_product"
          type="button"
          class="btn-produ-slide flex items-center justify-center btn-red-bg text-nowrap p-0 h-[50px] w-[50px] text-lg"
          :class="{
            'opacity-50 cursor-not-allowed':
              !product.available || isAddToCartProcessing,
          }"
          data-testid="add-to-cart-button"
          :disabled="!product.available"
          @click="addToCartProxy"
        >
          <i v-if="!isAddToCartProcessing" class="fa-solid fa-cart-plus"></i>
          <span v-else>
            <i class="fa-solid fa-spinner animate-spin"></i>
          </span>
        </button>

        <RouterLink
          v-else
          :to="formatLink(getProductRoute(product))"
          class="btn-produ-slide btn-red-bg"
        >
          {{ $t("product.details") }}
        </RouterLink>
      </div>
    </div>

    <i
      v-if="isLoading"
      class="prod-linke-loader absolute top-2 right-2 md:right-5 bg-transparent border-0 fa-solid fa-spinner"
    />

    <button
      v-if="!isLoading"
      class="prod-linke absolute top-2 right-2 md:right-5 bg-transparent border-0"
      aria-label="Add to wishlist"
      type="button"
      data-testid="product-box-toggle-wishlist-button"
      @click="toggleWishlistProduct"
    >
      <span v-if="isInWishlist">
        <img src="assets/icon/heart-red-icon.svg" alt="like icon" />
      </span>
      <span v-else>
        <img src="assets/icon/heart-black-icon.svg" alt="like icon" />
      </span>
    </button>
  </div>
</template>
